import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { Product, ProductVariant, getProductMainImage } from '../types';
import { useCart } from '../contexts/CartContext';
import toast from 'react-hot-toast';
import { ShareIcon, StarIcon, ChevronLeftIcon } from '@heroicons/react/24/outline';
import { ImageViewer } from "../components/ImageViewer";
import { Helmet } from "react-helmet-async";
import { ReviewsSection } from "../components/ReviewsSection";
import { reviewService } from "../services/reviewService";
import { WishlistButton } from "../components/WishlistButton";
import { useNetworkStatus } from "../hooks/useNetworkStatus";
import { CachedProduct, productCache } from "../hooks/productDB";
import { productViewService } from "../services/productViewService";
import { PriceDisplay } from '../components/PriceDisplay';

interface ProductWithVariants extends Product {
    variants: ProductVariant[];
}

interface VariantProduct {
    id: number;
    name: string;
    color: string;
    images: string[];
    main_image: string;
    product_code: string;
}

const getVariantDisplayValue = (variant: ProductVariant): string => {
    if (variant.size_or_volume) return variant.size_or_volume;

    if (variant.attributes && typeof variant.attributes === 'object') {
        const attrs = variant.attributes;
        if (Array.isArray(attrs) && attrs.length > 0) {
            const displayParts: string[] = [];
            for (const attr of attrs) {
                if (attr.type && attr.value) {
                    let displayValue = '';
                    switch (attr.type) {
                        case 'size':       displayValue = `${attr.value}`; break;
                        case 'volume':     displayValue = `${attr.value}${attr.unit ? ` ${attr.unit}` : ''}`; break;
                        case 'weight':     displayValue = `${attr.value}${attr.unit ? ` ${attr.unit}` : ''}`; break;
                        case 'dimensions': displayValue = `${attr.value}${attr.unit ? ` ${attr.unit}` : ''}`; break;
                        case 'other':      displayValue = `${attr.name || 'Другое'}: ${attr.value}`; break;
                        default:           displayValue = attr.value;
                    }
                    displayParts.push(displayValue);
                }
            }
            if (displayParts.length > 0) return displayParts.join(', ');
        }
    }

    if (variant.color) return variant.color;
    return variant.instance_id || 'Стандарт';
};

export const ProductDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const { addToCart } = useCart();

    const [product, setProduct] = useState<ProductWithVariants | null>(null);
    const [variants, setVariants] = useState<ProductVariant[]>([]);
    const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(null);
    const [loading, setLoading] = useState(true);
    const [quantity, setQuantity] = useState(1);
    const [selectedImage, setSelectedImage] = useState<string>('');
    const [activeTab, setActiveTab] = useState<'description' | 'specs' | 'reviews'>('description');
    const [relatedProducts, setRelatedProducts] = useState<VariantProduct[]>([]);
    const [showImageViewer, setShowImageViewer] = useState(false);
    const [viewerStartIndex, setViewerStartIndex] = useState(0);
    const [productRating, setProductRating] = useState<{ average: number; total: number }>({ average: 0, total: 0 });
    const [variantDescription, setVariantDescription] = useState<string>('');
    const isOnline = useNetworkStatus();

    const selectedVariantIdFromCart = (location.state as any)?.selectedVariantId;

    const cheapestVariant = useMemo(() => {
        if (!variants || variants.length === 0) return null;
        return variants.reduce((cheapest, current) =>
            current.price < cheapest.price ? current : cheapest, variants[0]);
    }, [variants]);

    // ── Шаринг ──────────────────────────────────────────────────────────────
    const copyToClipboard = async (url: string) => {
        try {
            await navigator.clipboard.writeText(url);
            toast.success('Ссылка скопирована!');
        } catch {
            const textarea = document.createElement('textarea');
            textarea.value = url;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
            toast.success('Ссылка скопирована!');
        }
    };

    const handleShare = async () => {
        const productUrl = `https://ruyo-market.web.app/product/${product?.id}`;
        if (navigator.share) {
            try {
                await navigator.share({ title: product?.name || 'Ruyo-market', text: `Посмотрите товар: ${product?.name}`, url: productUrl });
            } catch (error) {
                if ((error as Error).name !== 'AbortError') copyToClipboard(productUrl);
            }
        } else {
            copyToClipboard(productUrl);
        }
    };

    // ── Рейтинг ──────────────────────────────────────────────────────────────
    useEffect(() => {
        if (selectedVariant) loadVariantRating();
        else if (product) loadProductRating();
    }, [selectedVariant, product]);

    useEffect(() => {
        if (selectedVariant?.description) setVariantDescription(selectedVariant.description);
        else if (product?.description) setVariantDescription(product.description);
        else setVariantDescription('');
    }, [selectedVariant, product]);

    const loadVariantRating = async () => {
        if (!selectedVariant || !isOnline) return;
        try {
            const rating = await reviewService.getVariantRating(selectedVariant.id);
            setProductRating({ average: rating?.average_rating || 0, total: rating?.total_reviews || 0 });
        } catch (err: any) {
            console.warn('loadVariantRating error:', err?.message);
        }
    };

    const loadProductRating = async () => {
        if (!product || !isOnline) return;
        try {
            const rating = await reviewService.getProductRating(product.id);
            setProductRating({ average: rating?.average_rating || 0, total: rating?.total_reviews || 0 });
        } catch (err: any) {
            console.warn('loadProductRating error:', err?.message);
        }
    };

    // ── Выбор варианта ───────────────────────────────────────────────────────
    const openImageViewer = (index: number) => { setViewerStartIndex(index); setShowImageViewer(true); };

    useEffect(() => {
        if (id) fetchProduct();
    }, [id]);

    useEffect(() => {
        if (variants.length > 0) {
            if (selectedVariantIdFromCart) {
                const variantFromCart = variants.find(v => v.id === selectedVariantIdFromCart);
                if (variantFromCart) { setSelectedVariant(variantFromCart); return; }
            }
            if (cheapestVariant) setSelectedVariant(cheapestVariant);
        }
    }, [variants, selectedVariantIdFromCart, cheapestVariant]);

    const getImageFromPartial = (item: { images?: string[] }): string =>
        item.images && item.images.length > 0 ? item.images[0] : 'https://via.placeholder.com/300';

    // ── Загрузка товара ──────────────────────────────────────────────────────
    const fetchProduct = async () => {
        try {
            setLoading(true);

            if (!isOnline) {
                const cached = await productCache.getAll();
                const found = cached.find((p: CachedProduct) => p.id === Number(id));
                if (found) {
                    setProduct(found as any);
                    setVariants(found.variants || []);
                    if (found.images?.length) setSelectedImage(found.images[0]);
                    if (found.id) await productViewService.trackProductView(found.id);
                } else {
                    toast.error('Товар не найден в кэше');
                }
                setLoading(false);
                return;
            }

            const { data: productData, error: productError } = await supabase
                .from('products')
                .select(`*, variants:product_variants(*)`)
                .eq('id', id)
                .single();

            if (productError) {
                console.error('supabase error:', productError.message);
                toast.error('Ошибка загрузки товара');
                navigate('/products');
                return;
            }
            if (!productData) {
                toast.error('Товар не найден');
                navigate('/products');
                return;
            }

            setProduct(productData);
            setVariants(productData.variants || []);
            if (productData.images?.length) setSelectedImage(productData.images[0]);
            await productViewService.trackProductView(productData.id);
            await fetchRelatedProducts(productData);

        } catch (error: any) {
            console.error('Unexpected error:', error?.message);
            try {
                const cached = await productCache.getAll();
                const found = cached.find((p: CachedProduct) => p.id === Number(id));
                if (found) {
                    setProduct(found as any);
                    setVariants(found.variants || []);
                    if (found.images?.length) setSelectedImage(found.images[0]);
                    return;
                }
            } catch {}
            toast.error('Ошибка загрузки товара');
            navigate('/products');
        } finally {
            setLoading(false);
        }
    };

    const fetchRelatedProducts = async (currentProduct: ProductWithVariants) => {
        if (!isOnline || !currentProduct.family_code) return;
        try {
            const { data, error } = await supabase
                .from('products')
                .select(`id, name, color, images, product_code`)
                .eq('family_code', currentProduct.family_code)
                .eq('status', 'approved')
                .neq('id', currentProduct.id)
                .order('color');
            if (error) { console.warn('fetchRelatedProducts error:', error.message); return; }
            if (!data?.length) return;
            const formatted: VariantProduct[] = data.map(item => ({
                id: item.id, name: item.name, color: item.color || 'Стандарт',
                images: item.images || [], main_image: getImageFromPartial(item),
                product_code: item.product_code || '',
            }));
            const unique = formatted.filter((p, i, self) => i === self.findIndex(x => x.id === p.id));
            setRelatedProducts(unique);
        } catch (error: any) {
            console.warn('fetchRelatedProducts error:', error?.message);
        }
    };

    // ── Действия ─────────────────────────────────────────────────────────────
    const handleVariantChange = (productId: number) => navigate(`/product/${productId}`);
    const handleVariantSelect = (variant: ProductVariant) => { setSelectedVariant(variant); setQuantity(1); };
    const handleAddToCart = async () => {
        if (product && selectedVariant) await addToCart(product.id, selectedVariant.id, quantity);
    };

    const getCurrentStock = (): number => {
        if (selectedVariant) return selectedVariant.quantity;
        if (cheapestVariant) return cheapestVariant.quantity;
        return product?.stock_quantity || 0;
    };

    const getStockStatus = (stock: number) => {
        if (stock === 0)  return { text: 'Нет в наличии',    color: '#ef4444', bgColor: '#fee2e2' };
        if (stock < 5)   return { text: 'Критически мало',   color: '#dc2626', bgColor: '#fee2e2' };
        if (stock < 10)  return { text: 'Мало осталось',     color: '#f59e0b', bgColor: '#fffbeb' };
        if (stock < 20)  return { text: 'Заканчивается',     color: '#10b981', bgColor: '#ecfdf5' };
        return               { text: 'Много',                color: '#059669', bgColor: '#ecfdf5' };
    };

    const currentStock   = getCurrentStock();
    const stockStatus    = getStockStatus(currentStock);
    const stockPercentage = Math.min((currentStock / 100) * 100, 100);
    const mainImage      = selectedImage || (product ? getProductMainImage(product) : '');

    // ← цена в TJS — PriceDisplay сам конвертирует
    const selectedVariantPrice = selectedVariant ? selectedVariant.price : (cheapestVariant?.price || 0);

    // ── Рендер ───────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <div className="loader-container">
                <div className="spinner"></div>
                <p>Загрузка товара...</p>
            </div>
        );
    }

    if (!product) {
        return (
            <div className="container">
                <div className="empty-state">
                    <div className="empty-state-icon">📦</div>
                    <h3>Товар не найден</h3>
                    <button onClick={() => navigate('/products')} className="auth-button">
                        Вернуться в каталог
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>{product.name} | Ruyo-market</title>
                <meta name="description" content={product.description?.substring(0, 160) || 'Интернет магазин товаров с доставкой'} />
                <meta property="og:title" content={product.name} />
                <meta property="og:description" content={product.description?.substring(0, 160) || 'Интернет магазин товаров с доставкой'} />
                <meta property="og:image" content={product.images?.[0] || 'https://ruyo-market.web.app/og-image.jpg'} />
                <meta property="og:url" content={`https://ruyo-market.web.app/product/${product.id}`} />
                <meta property="og:type" content="product" />
                <meta property="og:site_name" content="Ruyo-market" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={product.name} />
                <meta name="twitter:description" content={product.description?.substring(0, 160) || 'Интернет магазин товаров с доставкой'} />
                <meta name="twitter:image" content={product.images?.[0] || 'https://ruyo-market.web.app/og-image.jpg'} />
            </Helmet>

            <div className="product-detail-header">
                <button onClick={() => navigate(-1)} className="detail-back-btn">
                    <ChevronLeftIcon style={{ width: 24, height: 24 }} />
                </button>
                <h1 className="detail-header-title" title={product.name}>{product.name}</h1>
                <div className="detail-actions-wb">
                    <button className="action-btn-wb" onClick={handleShare}>
                        <ShareIcon style={{ width: 20, height: 20 }} />
                    </button>
                    {selectedVariant && <WishlistButton variantId={selectedVariant.id} size="medium" />}
                </div>
            </div>

            <div className="product-detail-wb">
                {!isOnline && (
                    <div className="offline-banner" style={{ margin: '0 0 12px' }}>
                        📵 Нет подключения — данные из кэша
                    </div>
                )}

                {/* Галерея */}
                <div className="product-gallery-wb">
                    <div className="main-image-wb" onClick={() => {
                        const idx = product.images?.findIndex(img => img === selectedImage) || 0;
                        openImageViewer(idx);
                    }}>
                        <img src={mainImage} alt={product.name} />
                    </div>
                    {product.images && product.images.length > 1 && (
                        <div className="thumbnails-wb">
                            {product.images.map((img, index) => (
                                <div
                                    key={index}
                                    className={`thumbnail-wb ${selectedImage === img ? 'active' : ''}`}
                                    onClick={() => setSelectedImage(img)}
                                >
                                    <img src={img} alt={`${product.name} ${index + 1}`} />
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {showImageViewer && (
                    <ImageViewer
                        images={product.images || [mainImage]}
                        initialIndex={viewerStartIndex}
                        onClose={() => setShowImageViewer(false)}
                        productName={product.name}
                        productPrice={selectedVariantPrice}
                        productCode={product.product_code}
                        productDate={new Date().toLocaleDateString('ru-RU')}
                    />
                )}

                {/* Информация */}
                <div className="product-info-wb-detail">

                    {/* Бренд и рейтинг */}
                    <div className="brand-rating-wb">
                        {product.brand && <span className="brand-wb">{product.brand}</span>}
                        {productRating.total > 0 && (
                            <div className="rating-wb">
                                <StarIcon style={{ width: 16, height: 16, fill: '#f59e0b', color: '#f59e0b' }} />
                                <span>{productRating.average.toFixed(1)}</span>
                                <span className="reviews-count">
                                    ({productRating.total} {
                                    productRating.total === 1 ? 'отзыв'
                                        : productRating.total < 5 ? 'отзыва'
                                            : 'отзывов'
                                })
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Другие варианты */}
                    {relatedProducts.length > 0 && (
                        <div className="variants-section-wb">
                            <div className="variants-title-wb">Другие варианты:</div>
                            <div className="variants-thumbnails-wb">
                                {relatedProducts.map(variant => (
                                    <button
                                        key={variant.id}
                                        onClick={() => handleVariantChange(variant.id)}
                                        className="variant-thumbnail-wb"
                                        title={variant.name}
                                    >
                                        <div className="variant-thumbnail-image">
                                            <img src={variant.main_image} alt={variant.name} loading="lazy" />
                                        </div>
                                        <span className="variant-thumbnail-name">{variant.color || variant.name}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {product.product_code && <div className="sku-wb">Артикул: {product.product_code}</div>}
                    {product.color && <div className="color-info-wb">Цвет: {product.color}</div>}

                    {/* ✅ ЦЕНА С КОНВЕРТЕРОМ ВАЛЮТ */}
                    <div className="price-section-wb" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                        <PriceDisplay
                            priceInTJS={selectedVariantPrice}
                            size="22px"
                        />
                    </div>

                    {/* Выбранный вариант */}
                    {selectedVariant && (
                        <div className="selected-variant-info-wb">
                            <span className="selected-variant-label">Выбранный вариант:</span>
                            <span className="selected-variant-value">
                                {getVariantDisplayValue(selectedVariant)}
                            </span>
                        </div>
                    )}

                    {/* Варианты */}
                    {variants.length > 0 && (
                        <div className="variants-section-wb">
                            <div className="variants-title-wb">Варианты:</div>
                            <div className="variants-compact-grid">
                                {variants.map(variant => (
                                    <button
                                        key={variant.id}
                                        onClick={() => handleVariantSelect(variant)}
                                        className={`variant-compact-btn ${selectedVariant?.id === variant.id ? 'active' : ''}`}
                                    >
                                        <span className="variant-compact-value">{getVariantDisplayValue(variant)}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Остатки */}
                    <div className="stock-section-wb">
                        <div className="stock-header-wb">
                            <span className="stock-label-wb">Наличие:</span>
                            <span className="stock-status-wb" style={{
                                color: stockStatus.color, backgroundColor: stockStatus.bgColor,
                                padding: '4px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: '500',
                            }}>
                                {stockStatus.text}
                            </span>
                        </div>
                        <div className="progress-bar-wb">
                            <div className="progress-fill-wb" style={{
                                width: `${stockPercentage}%`,
                                backgroundColor: stockStatus.color,
                                transition: 'width 0.3s ease',
                            }} />
                        </div>
                        <div className="stock-count-wb">
                            {currentStock > 0 ? `${currentStock} шт. в наличии` : 'Товар закончился'}
                        </div>
                    </div>

                    {/* Корзина */}
                    {currentStock > 0 && (
                        <div className="cart-section-wb">
                            <div className="quantity-selector-wb">
                                <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="quantity-btn-wb" disabled={quantity <= 1}>-</button>
                                <span className="quantity-value-wb">{quantity}</span>
                                <button onClick={() => setQuantity(Math.min(currentStock, quantity + 1))} className="quantity-btn-wb" disabled={quantity >= currentStock}>+</button>
                            </div>
                            <button onClick={handleAddToCart} className="add-to-cart-btn-wb">
                                Добавить в корзину
                            </button>
                        </div>
                    )}

                    {/* Табы */}
                    <div className="tabs-wb">
                        {(['description', 'specs', 'reviews'] as const).map(tab => (
                            <button
                                key={tab}
                                className={`tab-btn-wb ${activeTab === tab ? 'active' : ''}`}
                                onClick={() => setActiveTab(tab)}
                            >
                                {tab === 'description' ? 'Описание' : tab === 'specs' ? 'Характеристики' : 'Отзывы'}
                            </button>
                        ))}
                    </div>

                    <div className="tab-content-wb">
                        {activeTab === 'description' && (
                            <div className="description-wb">
                                {variantDescription ? (
                                    <div className="variant-description-simple">
                                        {selectedVariant?.description && (
                                            <p className="variant-description-full">{selectedVariant.description}</p>
                                        )}
                                    </div>
                                ) : product.description ? (
                                    <p className="product-description-text">{product.description}</p>
                                ) : (
                                    <p className="no-description">Описание отсутствует</p>
                                )}
                            </div>
                        )}

                        {activeTab === 'specs' && (
                            <div className="specs-wb">
                                <div className="spec-group-wb">
                                    <div className="specs-section">
                                        <div className="specs-section-title">Основные характеристики</div>
                                        {[
                                            ['Бренд', product.brand],
                                            ['Артикул', product.product_code],
                                            ['Цвет', product.color],
                                            ['Страна производства', product.country],
                                        ].map(([label, value]) => (
                                            <div key={label} className="spec-item-wb">
                                                <span className="spec-label-wb">{label}</span>
                                                <span className="spec-value-wb">{value || 'Не указан'}</span>
                                            </div>
                                        ))}
                                    </div>

                                    {(product.composition || product.material) && (
                                        <div className="specs-section">
                                            <div className="specs-section-title">Состав и материалы</div>
                                            {product.composition && (
                                                <div className="spec-item-wb">
                                                    <span className="spec-label-wb">Состав</span>
                                                    <span className="spec-value-wb">{product.composition}</span>
                                                </div>
                                            )}
                                            {product.material && (
                                                <div className="spec-item-wb">
                                                    <span className="spec-label-wb">Материал</span>
                                                    <span className="spec-value-wb">{product.material}</span>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {(product.size_or_volume || product.quantity_per_pack) && (
                                        <div className="specs-section">
                                            <div className="specs-section-title">Размеры и объёмы</div>
                                            {product.size_or_volume && (
                                                <div className="spec-item-wb">
                                                    <span className="spec-label-wb">Размер/Объём</span>
                                                    <span className="spec-value-wb">{product.size_or_volume}</span>
                                                </div>
                                            )}
                                            {product.quantity_per_pack && (
                                                <div className="spec-item-wb">
                                                    <span className="spec-label-wb">Количество в упаковке</span>
                                                    <span className="spec-value-wb">{product.quantity_per_pack} шт.</span>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {(product.season || product.gender) && (
                                        <div className="specs-section">
                                            <div className="specs-section-title">Сезон и пол</div>
                                            {product.season && (
                                                <div className="spec-item-wb">
                                                    <span className="spec-label-wb">Сезон</span>
                                                    <span className="spec-value-wb">
                                                        {{ spring: 'Весна', summer: 'Лето', autumn: 'Осень', winter: 'Зима', demi: 'Демисезон', all: 'Все сезоны' }[product.season] || product.season}
                                                    </span>
                                                </div>
                                            )}
                                            {product.gender && (
                                                <div className="spec-item-wb">
                                                    <span className="spec-label-wb">Пол</span>
                                                    <span className="spec-value-wb">
                                                        {{ male: 'Мужской', female: 'Женский', unisex: 'Унисекс', children: 'Детский' }[product.gender] || product.gender}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {activeTab === 'reviews' && (
                            <ReviewsSection
                                productId={product.id}
                                variantId={selectedVariant?.id}
                                productName={product.name}
                            />
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};